import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { Badge } from "../../shared/Badge";
import {
  renderCustomColumnDefs,
  renderValidations,
} from "../../shared/Table/table.utils";

// Config for table and form
/**
 * Return badge color for a feedback status value.
 * @param {string} status
 */
const getStatusColor = (status) => {
  const normalized = (status || "").toLowerCase();
  if (["resolved", "closed"].includes(normalized)) return "green";
  if (["in progress"].includes(normalized)) return "blue";
  if (["in review"].includes(normalized)) return "yellow";
  return "gray";
};

/**
 * Return badge color for a priority value.
 * @param {string} priority
 */
const getPriorityColor = (priority) => {
  const normalized = (priority || "").toLowerCase();
  if (normalized === "critical") return "red";
  if (normalized === "high") return "orange";
  if (normalized === "medium") return "yellow";
  if (normalized === "low") return "green";
  return "gray";
};

/**
 * Safely format a date string for table display.
 * @param {string} dateValue
 */
const formatDate = (dateValue) => {
  if (!dateValue) return "—";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
};

const tableConfig = [
  {
    id: "feedbackId",
    cell: ({ row: { original }, getValue }) => {
      return (
        <Link
          className="text-primary-600 hover:underline"
          to={`/feedback/${original._id}`}
        >
          {getValue()}
        </Link>
      );
    },
  },
  {
    id: "subject",
    cell: ({ getValue }) => getValue() || "—",
  },
  {
    id: "author",
    cell: ({ getValue }) => {
      if (!getValue()) return;
      return getValue().fullName;
    },
  },
  {
    id: "parentCompany",
    cell: ({ row: { original }, getValue }) => {
      return getValue().name;
    },
  },
  {
    id: "type",
    cell: ({ getValue }) => getValue() || "—",
  },
  {
    id: "status",
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        <Badge color={getStatusColor(value)} label={value || "New"} size="sm" />
      );
    },
  },
  {
    id: "priority",
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        <Badge
          color={getPriorityColor(value)}
          label={value || "N/A"}
          size="sm"
        />
      );
    },
  },
  {
    id: "impact",
    cell: ({ getValue }) => getValue() || "—",
  },
  {
    id: "category",
    cell: ({ getValue }) => getValue() || "—",
  },
  {
    id: "dateCreated",
    cell: ({ getValue }) => formatDate(getValue()),
  },
  {
    id: "resolved",
    cell: ({ row: { original }, getValue }) => {
      return getValue() ? (
        <CheckCircleIcon className="w-5 h-5 text-green-500" />
      ) : (
        <XCircleIcon className="w-5 h-5 text-red-500" />
      );
    },
  },
];

export const COLUMN_DEFINITIONS = renderCustomColumnDefs(tableConfig);
export const FORM_OPTIONS = { validations: renderValidations(tableConfig) };
export const PAGE_SIZE_OPTIONS = [
  { label: "10 Tickets", value: 10 },
  { label: "20 Tickets", value: 20 },
  { label: "50 Tickets", value: 50 },
  { label: "100 Tickets", value: 100 },
  { label: "150 Tickets", value: 150 },
];
